import React, { useEffect, useState } from "react";
import { Lock, Heart } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import RevealSection from "@/components/RevealSection";

const TARGET = new Date("2026-06-22T15:00:00").getTime();
const pad = (n) => String(n).padStart(2, "0");

const LockedScreen = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, TARGET - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff / 3_600_000) % 24);
  const minutes = Math.floor((diff / 60_000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return (
    <section data-testid="surprise-locked" className="min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-20">
      <div className="max-w-xl w-full text-center">
        <div className="w-28 h-28 mx-auto rounded-full bg-rose-900/30 border border-rose-700/40 flex items-center justify-center mb-8">
          <Lock className="w-12 h-12 text-rose-400" />
        </div>
        <p className="font-accent text-4xl text-rose-300 mb-2">Patience, Amor ♡</p>
        <h1 className="font-serif text-4xl md:text-5xl text-rose-50">Cette page t'est réservée pour plus tard</h1>
        <p className="text-rose-200/70 mt-4 max-w-md mx-auto">
          Tout sera révélé le <span className="text-rose-300 font-medium">lundi 22 juin 2026 à 15h00</span>.
        </p>

        <div className="grid grid-cols-4 gap-3 md:gap-4 max-w-md mx-auto mt-10">
          {[
            { label: "Jours", value: pad(days) },
            { label: "Heures", value: pad(hours) },
            { label: "Min", value: pad(minutes) },
            { label: "Sec", value: pad(seconds) },
          ].map((u) => (
            <div key={u.label} className="bg-[#1A0A0E]/70 border border-rose-900/40 rounded-xl py-4">
              <div className="font-serif text-2xl md:text-3xl text-rose-50 tabular-nums">{u.value}</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-rose-300/60 mt-1">{u.label}</div>
            </div>
          ))}
        </div>

        <Link to="/">
          <Button className="mt-10 bg-rose-600 hover:bg-rose-500 text-white rounded-full h-12 px-7">
            <Heart className="w-4 h-4 mr-2" fill="currentColor" />
            Retour à l'accueil
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default function SurprisePage() {
  const { isAdmin, checking } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reveal/content");
        setStatus(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (checking || loading) {
    return (
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center">
        <Heart className="w-10 h-10 text-rose-500 animate-pulse" fill="currentColor" />
      </div>
    );
  }

  // Admin → always show the section (configure + preview).
  if (isAdmin) {
    return <RevealSection adminMode />;
  }

  // Non-admin → only show after unlock; otherwise locked screen.
  if (status?.unlocked) {
    return <RevealSection adminMode={false} />;
  }
  return <LockedScreen />;
}
